import {
  CUSTOM_DISCLAIMERS_API_PATH,
  CUSTOM_DISCLAIMERS_ENROLLMENT_LINK_API_PATH,
} from '../constants/customDisclaimers';
import { policyService } from './policyService';
import { apiClient } from './apiClient';
import { ServiceError } from './ServiceError';
import { CustomDisclaimerServiceError } from './customDisclaimerServiceError';
import type {
  CustomDisclaimer,
  CustomDisclaimerApiRecord,
  CustomDisclaimerService,
  CustomDisclaimerSummary,
  DisclaimerEnrollmentLinkResponse,
  DisclaimerDraft,
  DisclaimerPolicyOption,
} from '../types/customDisclaimers';
import {
  collectDisclaimerPolicyCandidates,
  isCustomDisclaimerApiRecord,
  toCustomDisclaimer,
  toCustomDisclaimerApiWriteRequest,
  toDisclaimerPolicyOptions,
} from '../utils/customDisclaimerApiUtils';

export class CustomDisclaimerApiService implements CustomDisclaimerService {
  private readonly listeners = new Set<() => void>();
  private listRequest: Promise<CustomDisclaimer[]> | null = null;

  async list(): Promise<CustomDisclaimer[]> {
    this.listRequest ??= this.loadList();

    const request = this.listRequest;
    try {
      return await request;
    } finally {
      if (this.listRequest === request) {
        this.listRequest = null;
      }
    }
  }

  private async loadList(): Promise<CustomDisclaimer[]> {
    const response = await apiClient.get<CustomDisclaimerApiRecord[]>(CUSTOM_DISCLAIMERS_API_PATH);
    if (!Array.isArray(response.data) || !response.data.every(isCustomDisclaimerApiRecord)) {
      throw new Error('The Custom Disclaimers API returned an invalid list response.');
    }

    return response.data.map((record) => toCustomDisclaimer(record));
  }

  async getById(id: string): Promise<CustomDisclaimer | null> {
    const disclaimers = await this.list();
    return disclaimers.find((disclaimer) => disclaimer.id === id) ?? null;
  }

  async getPolicyOptions(currentDisclaimerId?: string): Promise<DisclaimerPolicyOption[]> {
    const [policies, disclaimers] = await Promise.all([
      policyService.getPolicies(),
      this.list(),
    ]);

    return toDisclaimerPolicyOptions(
      collectDisclaimerPolicyCandidates(policies, disclaimers),
      disclaimers,
      currentDisclaimerId,
    );
  }

  async getSummary(): Promise<CustomDisclaimerSummary> {
    const disclaimers = await this.list();
    const assigned = disclaimers.filter((disclaimer) => disclaimer.policyIds.length > 0);

    return {
      total: disclaimers.length,
      assigned: assigned.length,
      unassigned: disclaimers.length - assigned.length,
    };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async getDisclaimerEnrollmentLink(): Promise<string> {
    const response = await apiClient.get<DisclaimerEnrollmentLinkResponse>(
      CUSTOM_DISCLAIMERS_ENROLLMENT_LINK_API_PATH,
    );
    const consentUrl = response.data?.consentUrl;

    if (typeof consentUrl !== 'string' || !consentUrl.trim()) {
      throw new ServiceError('Unable to load the custom disclaimer enrollment link. Please try again.', {
        code: 'CUSTOM_DISCLAIMER_CONSENT_URL_MALFORMED',
      });
    }

    return consentUrl;
  }

  async create(draft: DisclaimerDraft): Promise<CustomDisclaimer> {
    const request = toCustomDisclaimerApiWriteRequest(draft);

    try {
      const response = await apiClient.post<unknown>(CUSTOM_DISCLAIMERS_API_PATH, request);
      this.invalidateListRequest();
      const disclaimer = await this.resolveWriteResult(response.data, request.name);
      this.notifyListeners();
      return disclaimer;
    } catch (error) {
      throw this.toServiceError(error);
    }
  }

  async update(id: string, draft: DisclaimerDraft): Promise<CustomDisclaimer> {
    const request = toCustomDisclaimerApiWriteRequest(draft);

    try {
      const response = await apiClient.patch<unknown>(
        `${CUSTOM_DISCLAIMERS_API_PATH}/${encodeURIComponent(id)}`,
        request,
      );
      this.invalidateListRequest();
      const disclaimer = await this.resolveWriteResult(response.data, undefined, id);
      this.notifyListeners();
      return disclaimer;
    } catch (error) {
      throw this.toServiceError(error);
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete<unknown>(`${CUSTOM_DISCLAIMERS_API_PATH}/${encodeURIComponent(id)}`);
      this.invalidateListRequest();
      this.notifyListeners();
    } catch (error) {
      throw this.toServiceError(error);
    }
  }

  private async resolveWriteResult(
    responseData: unknown,
    createdName?: string,
    updatedId?: string,
  ): Promise<CustomDisclaimer> {
    if (isCustomDisclaimerApiRecord(responseData)) {
      return toCustomDisclaimer(responseData);
    }

    const disclaimers = await this.list();
    const disclaimer = updatedId
      ? disclaimers.find((candidate) => candidate.id === updatedId)
      : disclaimers.find((candidate) => candidate.name === createdName);

    if (!disclaimer) {
      throw new Error('The Custom Disclaimer was saved but could not be reloaded.');
    }

    return disclaimer;
  }

  private toServiceError(error: unknown): Error {
    if (error instanceof CustomDisclaimerServiceError) {
      return error;
    }

    let statusCode: number | undefined;
    if (error instanceof ServiceError) {
      statusCode = error.statusCode;
    } else if (typeof error === 'object' && error !== null && 'statusCode' in error) {
      statusCode = (error as { statusCode?: number }).statusCode;
    }

    if (statusCode === 409) {
      return new CustomDisclaimerServiceError(
        'duplicate-name',
        'A disclaimer with this name already exists.',
      );
    }
    if (statusCode === 404) {
      return new CustomDisclaimerServiceError(
        'not-found',
        'The disclaimer could not be found.',
      );
    }

    return error instanceof Error ? error : new Error('The Custom Disclaimer request failed.');
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }

  private invalidateListRequest(): void {
    this.listRequest = null;
  }
}